// @ts-nocheck
import { useEffect, useState } from 'react';
import {
  createMachine,
  state,
  state as final,
  state as initial,
  transition,
  reduce,
  guard,
  invoke,
  immediate,
} from 'robot3';
import { createUseMachine } from 'robot-hooks';
import { goBackReducer, saveCart } from './functions';
import { ACTION_TYPES, STATE } from './constants.js';
import {
  hasPetName,
  hasPetWeight,
  hasPetActivity,
  hasPetGender,
  hasPetStatus,
  allPetsHaveProductInCart,
  submitCartFailed,
  isCompletePet,
  allPetsComplete,
  isAddPup,
  hasInitialPetWeight,
  hasInitialPetActivity,
  isInitialFlow,
  isEditPet,
  hasPet,
  isDeletePet,
  initialPetComplete,
  notAddPup,
  hasMealPrep,
  showGiftOnboardingStep,
} from './guard';

export const useMachine = createUseMachine(useEffect, useState);

const createInitialContext = ({
  customer = {},
  pet = {},
  pet_id = '',
  recipes = { fresh: null },
  extras = [],
  planType = null,
  stateKey = STATE.initial,
  flags = {}, // This may come in handy moving forward with V3
} = {}) => ({
  customer,
  pet,
  pet_id,
  recipes,
  extras,
  planType,
  stateKey,
  flags,
});

export const onboardingMachine = createMachine(
  STATE.initial,
  {
    [STATE.initial]: initial(
      /**
       ** Transitions to Pup Name Page by default if
       ** - isAddPup: true
       ** - allPetsHaveProductInCart: true
       */
      transition(
        ACTION_TYPES.initialLoad,
        STATE.pupName,
        guard(isAddPup),
        guard(allPetsHaveProductInCart),
        reduce((ctx, { payload }) => ({
          ...ctx,
          emailStepLast: payload.emailStepLast,
          flags: { ...ctx.flags, ...payload.flags },
          stateKey: STATE.pupName,
          customer: payload.customer,
          pets: payload.pets,
          pet: null,
          pet_id: '',
          isAddPup: true,
          isEditPet: false,
        })),
      ),
      /**
       ** Transitions to Pup Name Page for edit pet flow if
       ** - isEditPet: true
       ** - initialPetComplete: true
       ** - notAddPup: true
       */
      transition(
        ACTION_TYPES.initialLoad,
        STATE.pupName,
        guard(isEditPet),
        guard(initialPetComplete),
        guard(notAddPup),
        reduce((ctx, { payload }) => ({
          ...ctx,
          emailStepLast: payload.emailStepLast,
          flags: { ...ctx.flags, ...payload.flags },
          stateKey: STATE.pupName,
          customer: payload.customer,
          isEditPet: true,
          isAddPup: false,
          pets: payload.pets,
          pet:
            payload.pets.find(p => p.id === payload.pet_id) ?? payload.pets[0],
          pet_id: payload.pet_id,
        })),
      ),
      /**
       ** Transitions to Delete Pet state if
       ** - hasPet: true
       ** - isDeletePet: true
       ** - notAddPup: true
       */
      transition(
        ACTION_TYPES.initialLoad,
        STATE.deletePet,
        guard(hasPet),
        guard(isDeletePet),
        guard(notAddPup),
        reduce((ctx, { payload }) => ({
          ...ctx,
          flags: { ...ctx.flags, ...payload.flags },
          stateKey: STATE.deletePet,
          customer: payload.customer,
          pet_id: payload.pet_id,
          pet: payload.pets.find(p => p.id === payload.pet_id),
          deletePetFromCart: payload.deletePetFromCart,
        })),
      ),
      /**
       ** Transitions to Pup Info Page if page by default if
       ** - hasPet: true
       ** - notAddPup: true
       ** Pup Info then takes over and passes the pet to the correct step via immediate transitions
       */
      transition(
        ACTION_TYPES.initialLoad,
        STATE.pupName,
        guard(hasPet),
        guard(notAddPup),
        reduce((ctx, { payload }) => {
          let pet = null;
          if (payload.pets?.length) {
            pet =
              payload.pets.find(pet => !isCompletePet(pet)) ?? payload.pets[0];
          }
          const isEditPet = !!payload.isEditPet || !!ctx.isEditPet;

          return {
            ...ctx,
            stateKey: STATE.pupName,
            customer: payload.customer,
            pet: pet || payload.pet,
            pet_id: payload.pet_id,
            flags: { ...ctx.flags, ...(payload.flags || {}) },
            isEditPet: isEditPet,
            isAddPup: false,
          };
        }),
      ),
      /**
       ** Transitions to Intro Page
       ** Only show this when you first enter onboarding if
       ** - isInitialFlow: true
       ** - notAddPup: true
       */
      transition(
        ACTION_TYPES.initialLoad,
        STATE.intro,
        guard(isInitialFlow),
        guard(notAddPup),
        reduce((ctx, { payload }) => ({
          ...ctx,
          flags: { ...ctx.flags, ...payload.flags },
          stateKey: STATE.intro,
          isAddPup: false,
          isEditPet: false,
        })),
      ),
    ),
    [STATE.intro]: state(
      /**
       ** Route: Continue/Next
       *
       ** Transitions to PupName Page
       */
      transition(
        ACTION_TYPES.toPupName,
        STATE.pupName,
        reduce((ctx, { payload }) => ({
          ...ctx,
          stateKey: STATE.pupName,
          pet: payload?.pet,
          pet_id: payload?.pet?.id,
          flags: { ...ctx.flags, ...payload.flags },
        })),
      ),
    ),
    [STATE.pupName]: state(
      /**
       ** Immediately transition to PupGender if
       ** - isInitialFlow: true
       ** - notAddPup: true
       ** - hasPetName: true
       */
      immediate(
        STATE.pupGender,
        guard(isInitialFlow),
        guard(notAddPup),
        guard(hasPetName),
        reduce(ctx => ({
          ...ctx,
          stateKey: STATE.pupGender,
          initialFlow: true,
          pet_id: ctx?.pet?.id,
        })),
      ),
      /**
       ** Route: Back Button
       *
       ** Transitions to Intro Page
       */
      transition(
        ACTION_TYPES.goBack,
        STATE.intro,
        reduce(ctx => ({
          ...ctx,
          stateKey: STATE.intro,
        })),
        reduce(goBackReducer),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to PupGender Page
       */
      transition(
        ACTION_TYPES.toPupGender,
        STATE.pupGender,
        reduce((ctx, { payload }) => ({
          ...ctx,
          stateKey: STATE.pupGender,
          pet: payload.pet,
          pet_id: payload.pet.id,
          flags: { ...ctx.flags, ...payload.flags },
        })),
      ),
    ),
    [STATE.pupGender]: state(
      /**
       ** Immediately transition to PupStatus if
       ** - isInitialFlow: true
       ** - notAddPup: true
       ** - hasPetGender: true
       */
      immediate(
        STATE.pupStatus,
        guard(isInitialFlow),
        guard(notAddPup),
        guard(hasPetGender),
        reduce(ctx => ({
          ...ctx,
          stateKey: STATE.pupStatus,
          initialFlow: true,
          pet_id: ctx?.pet?.id,
        })),
      ),
      /**
       ** Route: Back Button
       *
       ** Transitions to PupName Page
       */
      transition(
        ACTION_TYPES.goBack,
        STATE.pupName,
        reduce(ctx => ({
          ...ctx,
          stateKey: STATE.pupName,
        })),
        reduce(goBackReducer),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to PupStatus Page
       */
      transition(
        ACTION_TYPES.toPupStatus,
        STATE.pupStatus,
        reduce((ctx, { payload }) => ({
          ...ctx,
          stateKey: STATE.pupStatus,
          pet: payload.pet,
          pet_id: payload.pet.id,
          flags: { ...ctx.flags, ...payload.flags },
        })),
      ),
    ),
    [STATE.pupStatus]: state(
      /**
       ** Immediately transition to Weight Page if
       ** - isInitialFlow: true
       ** - notAddPup: true
       ** - hasPetStatus: true
       */
      immediate(
        STATE.weight,
        guard(isInitialFlow),
        guard(notAddPup),
        guard(hasPetStatus),
        reduce(ctx => ({
          ...ctx,
          stateKey: STATE.weight,
          initialFlow: true,
          pet_id: ctx?.pet?.id,
        })),
      ),
      /**
       ** Route: Back Button
       *
       ** Transitions to PupGender Page
       */
      transition(
        ACTION_TYPES.goBack,
        STATE.pupGender,
        reduce(ctx => ({
          ...ctx,
          stateKey: STATE.pupGender,
        })),
        reduce(goBackReducer),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to Weight Page
       */
      transition(
        ACTION_TYPES.toWeight,
        STATE.weight,
        reduce((ctx, { payload }) => ({
          ...ctx,
          stateKey: STATE.weight,
          pet: payload.pet,
          pet_id: payload.pet.id,
          flags: { ...ctx.flags, ...payload.flags },
        })),
      ),
    ),
    [STATE.weight]: state(
      /**
       ** Immediately transition to Activity Page if
       ** - isInitialFlow: true
       ** - notAddPup: true
       ** - hasInitialPetWeight: true
       */
      immediate(
        STATE.activity,
        guard(isInitialFlow),
        guard(notAddPup),
        guard(hasInitialPetWeight),
        reduce(ctx => ({
          ...ctx,
          stateKey: STATE.activity,
          pet_id: ctx?.pet?.id,
          initialFlow: true,
        })),
      ),
      /**
       ** Route: Back Button
       *
       ** Transitions to PupStatus Page
       */
      transition(
        ACTION_TYPES.goBack,
        STATE.pupStatus,
        reduce(ctx => ({
          ...ctx,
          stateKey: STATE.pupStatus,
        })),
        reduce(goBackReducer),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to Activity Selection Page if
       ** - hasPetWeight: true
       */
      transition(
        ACTION_TYPES.toActivity,
        STATE.activity,
        guard(hasPetWeight),
        reduce((ctx, { payload }) => ({
          ...ctx,
          stateKey: STATE.activity,
          pet: payload.pet,
          flags: { ...ctx.flags, ...payload.flags },
        })),
      ),
    ),
    [STATE.activity]: state(
      /**
       ** Immediately transition to Allergies Page if
       ** - isInitialFlow: true
       ** - notAddPup: true
       ** - hasInitialPetActivity: true
       */
      immediate(
        STATE.allergies,
        guard(isInitialFlow),
        guard(notAddPup),
        guard(hasInitialPetActivity),
        reduce(ctx => ({
          ...ctx,
          stateKey: STATE.allergies,
          initialFlow: true,
          pet_id: ctx?.pet?.id,
        })),
      ),
      /**
       ** Route: Back Button
       *
       ** Transitions to Weight Page
       */
      transition(
        ACTION_TYPES.goBack,
        STATE.weight,
        reduce(ctx => ({
          ...ctx,
          stateKey: STATE.weight,
        })),
        reduce(goBackReducer),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to Allergies Page if
       ** - hasPetActivity: true
       */
      transition(
        ACTION_TYPES.toAllergies,
        STATE.allergies,
        guard(hasPetActivity),
        reduce((ctx, { payload }) => ({
          ...ctx,
          stateKey: STATE.allergies,
          isEditPet: ctx.isEditPet,
          pet: payload.pet,
          flags: { ...ctx.flags, ...payload.flags },
        })),
      ),
    ),
    [STATE.allergies]: state(
      /**
       ** Immediately transition to Recommended Plan if
       ** - notAddPup: true
       ** - hasMealPrep: true
       */
      immediate(
        STATE.recommendedPlan,
        guard(notAddPup),
        guard(hasMealPrep),
        reduce(ctx => ({
          ...ctx,
          stateKey: STATE.recommendedPlan,
          initialFlow: true,
          pet_id: ctx?.pet?.id,
        })),
      ),
      /**
       ** Route: Back Button
       *
       ** Transitions to Activity Selection Page
       */
      transition(
        ACTION_TYPES.goBack,
        STATE.activity,
        reduce(ctx => ({
          ...ctx,
          stateKey: STATE.activity,
        })),
        reduce(goBackReducer),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to Recommended Plan Page if
       ** - allPetsComplete: true
       */
      transition(
        ACTION_TYPES.toRecommendedPlan,
        STATE.recommendedPlan,
        guard(allPetsComplete),
        reduce((ctx, { payload }) => ({
          ...ctx,
          stateKey: STATE.recommendedPlan,
          planType: payload.planType,
          pet: payload.pet,
          flags: { ...ctx.flags, ...payload.flags },
        })),
      ),
    ),
    [STATE.recommendedPlan]: state(
      /**
       ** Route: Back Button
       *
       ** Transitions to Allergies Page
       */
      transition(
        ACTION_TYPES.goBack,
        STATE.allergies,
        reduce(
          (ctx, { payload }) => ({
            ...ctx,
            stateKey: STATE.allergies,
            planType: payload.planType,
            pet: payload.pet,
            flags: { ...ctx.flags, ...payload.flags },
          }),
          reduce(goBackReducer),
        ),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to submit cart function
       */
      transition(
        ACTION_TYPES.selectedFreshRecipes,
        STATE.submitCart,
        reduce((ctx, { payload }) => ({
          ...ctx,
          stateKey: STATE.recommendedPlan,
          planType: payload.planType,
          recipes: { ...ctx.recipes, fresh: payload.recipes },
          flags: { ...ctx.flags, ...payload.flags },
        })),
      ),
      transition(
        ACTION_TYPES.selectedMixedRecipes,
        STATE.submitCart,
        reduce((ctx, { payload }) => ({
          ...ctx,
          stateKey: STATE.recommendedPlan,
          planType: payload.planType,
          recipes: { dry: payload.recipes.dry, fresh: payload.recipes.fresh },
          flags: { ...ctx.flags, ...payload.flags },
        })),
      ),
    ),
    [STATE.submitCart]: invoke(
      saveCart,
      /**
       ** Transitions to recommended Plan Page if
       ** - submitCartFailed: true
       */
      transition(
        'done',
        STATE.recommendedPlan,
        guard(submitCartFailed),
        reduce((ctx, payload) => ({
          ...ctx,
          submitCartResult: payload.data,
          stateKey: STATE.recommendedPlan,
        })),
      ),
      /**
       ** Transitions to Gift With Purchase Page
       ** if showGiftOnboardingStep flag is true
       */
      transition(
        'done',
        STATE.giftWithPurchase,
        guard(showGiftOnboardingStep),
        reduce((ctx, payload) => ({
          ...ctx,
          submitCartResult: payload.data,
          stateKey: STATE.giftWithPurchase,
        })),
      ),
      /**
       ** Transitions to Extras Page
       */
      transition(
        'done',
        STATE.extras,
        reduce((ctx, payload) => ({
          ...ctx,
          submitCartResult: payload.data,
          stateKey: STATE.extras,
        })),
      ),
    ),
    [STATE.giftWithPurchase]: state(
      /**
       ** Route: Back Button
       **
       ** Transitions to Recommended Plan Page
       */
      transition(
        ACTION_TYPES.goBack,
        STATE.recommendedPlan,
        reduce(ctx => ({
          ...ctx,
          stateKey: STATE.recommendedPlan,
        })),
        reduce(goBackReducer),
      ),
      /**
       ** Route: Continue/Next
       **
       ** Transitions to Extras Page
       */
      transition(
        ACTION_TYPES.toExtras,
        STATE.extras,
        reduce(ctx => ({
          ...ctx,
          stateKey: STATE.extras,
        })),
      ),
    ),
    [STATE.extras]: state(
      /**
       ** Route: Back Button
       **
       ** Transitions to Gift With Purchase Page
       ** if showGiftOnboardingStep flag is true
       */
      transition(
        ACTION_TYPES.goBack,
        STATE.giftWithPurchase,
        guard(showGiftOnboardingStep),
        reduce(ctx => ({
          ...ctx,
          stateKey: STATE.giftWithPurchase,
        })),
        reduce(goBackReducer),
      ),
      transition(
        ACTION_TYPES.goBack,
        STATE.recommendedPlan,
        reduce(ctx => ({
          ...ctx,
          stateKey: STATE.recommendedPlan,
        })),
        reduce(goBackReducer),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to Checkout if
       ** - allPetsComplete: true
       */
      transition(
        ACTION_TYPES.toCheckout,
        STATE.checkout,
        guard(allPetsComplete),
        reduce((ctx, { payload }) => ({
          ...ctx,
          stateKey: STATE.checkout,
          extras: payload.extras,
          pet: payload.pet,
          pets: payload.pets,
          flags: { ...ctx.flags, ...payload.flags },
        })),
      ),
    ),
    [STATE.deletePet]: invoke(
      async ctx => {
        return ctx.deletePetFromCart(ctx.pet_id || ctx.pet.id);
      },
      transition(
        'done',
        STATE.checkout,
        reduce((ctx, { data }) => ({
          ...ctx,
          stateKey: STATE.checkout,
          cart: data.cart,
          pets: data.pets,
          pet: data.pets[0],
          pet_id: data.pets[0].id,
        })),
      ),
      transition(
        'error',
        STATE.checkout,
        reduce(ctx => ({
          ...ctx,
          stateKey: STATE.checkout,
        })),
      ),
    ),
    [STATE.checkout]: final(),
  },
  createInitialContext,
);
